import { FC, useState, useEffect, useRef, useCallback } from 'react';
import { UiIcon } from '@frontend/znanie-uikit';
import cn from 'classnames';

import * as T from './Hint.types';
import * as C from './Hint.const';
import css from './Hint.module.scss';

// TODO: доработать Hint:
// 1) позиционирование "контента", когда он не помещается в экран

export const Hint: FC<T.IHint> = ({
  timer = C.DEFAULT_SHOW_TIME_MS,
  orientation = 'left',
  ...props
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const hideTimeout = useRef<NodeJS.Timeout>();
  const resetTranslateTimeout = useRef<NodeJS.Timeout>();

  const [isShown, setShown] = useState(false);
  const [translateX, setTranslateX] = useState<number>(0);

  const switchShownHandler = useCallback(() => {
    if (!isShown) {
      setShown(true);
    }
  }, [isShown]);

  const outsideClickHandler = useCallback(
    (e: MouseEvent) => {
      if (
        e.target &&
        contentRef.current &&
        !contentRef.current.contains(e.target as Node)
      ) {
        hideHanlder();
      }
    },
    [contentRef.current]
  );

  const hideHanlder = () => {
    setShown(false);

    if (props.withTimer) {
      clearTimeout(hideTimeout.current);
    }
  };

  useEffect(() => {
    if (isShown) {
      if (props.withTimer) {
        clearTimeout(hideTimeout.current);

        hideTimeout.current = setTimeout(() => {
          hideHanlder();
        }, timer);
      }

      // Добавление обработчика в очередь макрозадач, чтобы
      // он не срабатывал сразу при установке (событие клика
      // вызывает обработчик моментально после установки)
      setTimeout(() => {
        document.body.addEventListener('click', outsideClickHandler);
      });
    }

    return () => {
      if (isShown) {
        document.body.removeEventListener('click', outsideClickHandler);
      }
    };
  }, [isShown, orientation, contentRef, setTranslateX]);

  useEffect(() => {
    if (isShown) {
      // Сбрасываем таймаут, если мы не успели ресетнуть смещение
      // Если мы не сбросим таймаут и вычислим новое смещение,
      // то после отработки таймаута смещение сброситься в 0
      if (resetTranslateTimeout.current) {
        clearTimeout(resetTranslateTimeout.current);
      }

      const bbox = contentRef.current?.getBoundingClientRect();

      if (orientation === 'left') {
        const offset =
          document.body.offsetWidth - ((bbox?.left ?? 0) + (bbox?.width ?? 0));

        if (offset < 0) {
          setTranslateX(offset - C.VIEWPORT_OFFSET);
        }
      } else if (orientation === 'right') {
        const offset = bbox?.left ?? 0;

        if (offset < 0) {
          setTranslateX(Math.abs(offset) + C.VIEWPORT_OFFSET);
        }
      }
    } else {
      // Ставим таймаут, который срабатывает после анимации исчезновения (opacity = 0)
      resetTranslateTimeout.current = setTimeout(() => {
        // Сбрасываем смещение контейнера при закрытии окна
        setTranslateX(0);
      }, C.OPACITY_ANIMATION_TIME_MS);
    }
  }, [isShown]);

  return (
    <div
      onClick={props.disabled ? undefined : switchShownHandler}
      className={css.container}
    >
      <div
        className={cn(
          css.content,
          isShown && css.isShown,
          orientation === 'left' && css.orientationLeft,
          orientation === 'right' && css.orientationRight
        )}
        style={{
          transform: `translate(${translateX}px, calc(-100% - 12px))`,
        }}
        ref={contentRef}
      >
        {!!props.withIcon && (
          <div className={css.iconWrapper}>
            <UiIcon name="mark-info" color="#426FFF" size={18} />
          </div>
        )}

        {props.content}
      </div>

      {props.children}
    </div>
  );
};
